import { cn } from "@gettactic/helpers/src/css/cn";
import { findOpenHourRulesForDates } from "@gettactic/helpers/src/reserve/openHourRules";
import { useResourceHourRules } from "@gettactic/hooks";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { useReserveForm } from "@/features/reserve/components/Reserve/ReserveFormProvider";
import { OpenRuleMessage } from "@/features/reserve/components/RestrictionAlert/OpenRuleMessage";
import { Alert } from "@/ui/index";

export interface ReserveFormOpenHourRuleAlertsProps {
  className?: string;
}

/**
 * Counterpart of ReserveFormRestrictionAlerts for "open" holiday and
 * custom-hours rules: those block nothing, so the availability data never
 * mentions them, but the user should still know the office (or the picked
 * resource) is open on a day they might assume is closed.
 */
export const ReserveFormOpenHourRuleAlerts: React.FC<
  ReserveFormOpenHourRuleAlertsProps
> = ({ className }) => {
  const { form, currentOfficeId, currentResource } = useReserveForm();
  const weekDates = form.watch("weekDates");
  const resourceId = currentResource?.id ?? null;
  const { rules } = useResourceHourRules(currentOfficeId, resourceId);

  const openRules = useMemo(() => {
    const dates = weekDates.map((date: Date) => format(date, "yyyy-MM-dd"));
    return findOpenHourRulesForDates(rules, dates, resourceId);
  }, [rules, weekDates, resourceId]);

  if (openRules.length === 0) {
    return null;
  }

  return (
    <div className={cn("mb-2 max-w-xl", className)}>
      <Alert
        variant="tacticAlert"
        alertMessage=""
        canDismiss={false}
        alertTitle="Some of the selected days have custom open hours"
        className="mt-2 px-4 py-2"
      >
        {openRules.map((rule) => (
          <OpenRuleMessage key={rule.id} rule={rule} />
        ))}
      </Alert>
    </div>
  );
};
